export type SocketV2State =
  | 'idle' // constructed, never opened
  | 'connecting' // handshake in flight
  | 'open'
  | 'reconnecting' // reconnect attempt scheduled or in flight
  | 'suspended' // deliberately not retrying: offline or flap cooldown
  | 'closing' // intentional close in flight
  | 'closed' // intentional close complete; open() restarts cleanly
  | 'failed'; // gave up: maxRetries exhausted or fatal close code

const LEGAL_TRANSITIONS: Record<SocketV2State, readonly SocketV2State[]> = {
  closed: ['connecting'],
  closing: ['closed'],
  connecting: ['open', 'reconnecting', 'suspended', 'closing', 'closed', 'failed'],
  failed: ['connecting'],
  idle: ['connecting'],
  open: ['reconnecting', 'suspended', 'closing', 'closed', 'failed'],
  reconnecting: ['connecting', 'reconnecting', 'suspended', 'closing', 'closed', 'failed'],
  suspended: ['connecting', 'reconnecting', 'closing', 'closed'],
};

export class SocketStateMachine {
  private current: SocketV2State = 'idle';

  constructor(private onIllegal?: (from: SocketV2State, to: SocketV2State) => void) {}

  public get state(): SocketV2State {
    return this.current;
  }

  public canTransition = (to: SocketV2State): boolean => LEGAL_TRANSITIONS[this.current].includes(to);

  public transition = (to: SocketV2State): boolean => {
    if (!this.canTransition(to)) {
      this.onIllegal?.(this.current, to);
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(`SocketV2: illegal state transition ${this.current} -> ${to}`);
      }
      return false;
    }
    this.current = to;
    return true;
  };
}
